import { computed } from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import type { TimesheetEntryLine } from './useTimesheetEntryDetail';

export interface ValidationResult {
  isValid: boolean;
  type: 'error' | 'warning';
  title: string;
  message: string;
  hasConfirmOption: boolean;
}

interface TimeRange {
  startMinutes: number;
  endMinutes: number;
  lineId?: string;
}

const toMinutes = (date: Date | undefined): number => {
  if (!date) return -1;
  return date.getHours() * 60 + date.getMinutes();
};

const normalizeRange = (start: number, end: number): { start: number; end: number } => {
  if (end < start) {
    return { start, end: end + 1440 };
  }
  return { start, end };
};

const rangesOverlap = (a: TimeRange, b: TimeRange): boolean => {
  const rangeA = normalizeRange(a.startMinutes, a.endMinutes);
  const rangeB = normalizeRange(b.startMinutes, b.endMinutes);
  return rangeA.start < rangeB.end && rangeB.start < rangeA.end;
};

export function useTimesheetValidation() {
  const store = useStore();

  const settings = computed(() => store.getters['setting/settings'] || {});

  // Calculate total minutes for lines
  const calculateTotalMinutes = (lines: TimesheetEntryLine[]): number => {
    let total = 0;
    for (const line of lines) {
      if (line.startTime && line.endTime) {
        let diff = dayjs(line.endTime).diff(dayjs(line.startTime), 'minute');
        if (diff <= 0) diff += 24 * 60;
        if (!Number.isNaN(diff)) total += diff;
      }
    }
    return Math.max(0, total);
  };

  // 1. Check current timesheet total hours
  const checkCurrentTimesheetTotalHours = (lines: TimesheetEntryLine[]): ValidationResult | null => {
    if (!settings.value.MaximumDailyHoursCheckingEnable) return null;
    
    const totalMinutes = calculateTotalMinutes(lines);
    const maxMinutes = (settings.value.MaximumDailyHours || 24) * 60;
    
    if (totalMinutes > maxMinutes) {
      return {
        isValid: false,
        type: 'error',
        title: 'Submitting',
        message: `Total hours logged in the timesheet\n is over ${settings.value.MaximumDailyHours} hours.\n Please review and adjust your tasks\n accordingly.`,
        hasConfirmOption: false
      };
    }
    return null;
  };

  // 2. Check combined total hours for all timesheets on the day
  const checkTimesheetTotalHours = async (
    lines: TimesheetEntryLine[],
    startDate: string,
    excludeTimesheetId?: number
  ): Promise<ValidationResult | null> => {
    if (!settings.value.MaximumDailyHoursCheckingEnable) return null;
    
    const currentMinutes = calculateTotalMinutes(lines);
    
    try {
      const existingTimesheets = await store.dispatch('v2/loadTimesheetsByDate', {
        date: dayjs(startDate).format('YYYY-MM-DD'),
        excludeId: excludeTimesheetId
      });
      
      let otherMinutes = 0;
      for (const ts of existingTimesheets || []) {
        const tsLines = ts.TimesheetLines || ts.timesheetLines || [];
        for (const line of tsLines) {
          otherMinutes += line.TimesheetLineMinutes || 0;
        }
      }
      
      const totalMinutes = currentMinutes + otherMinutes;
      const maxMinutes = (settings.value.MaximumDailyHours || 24) * 60;
      
      if (totalMinutes > maxMinutes) {
        return {
          isValid: false,
          type: 'error',
          title: 'Submitting',
          message: `The combined total hours for all\n timesheets on this day exceed ${settings.value.MaximumDailyHours} hours.\n Please review and adjust your\n timesheets.`,
          hasConfirmOption: false
        };
      }
    } catch (error) {
      // Error checking total hours
    }
    return null;
  };

  // 3. Check intra-timesheet overlap
  const checkIntraOverlap = (lines: TimesheetEntryLine[]): ValidationResult | null => {
    const validLines = lines.filter(l => l.startTime && l.endTime);
    const ranges: TimeRange[] = validLines.map(l => ({
      startMinutes: toMinutes(l.startTime),
      endMinutes: toMinutes(l.endTime),
      lineId: l.id
    }));

    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        if (rangesOverlap(ranges[i], ranges[j])) {
          return {
            isValid: false,
            type: 'error',
            title: 'Submitting',
            message: 'Overlapping tasks detected in the\n timesheet. Ensure there are no conflicts\n in your specified task times.',
            hasConfirmOption: false
          };
        }
      }
    }
    return null;
  };

  // 4. Check cross-timesheet overlap
  const checkCrossOverlap = async (
    lines: TimesheetEntryLine[],
    startDate: string,
    excludeTimesheetId?: number
  ): Promise<ValidationResult | null> => {
    if (!settings.value.TimesheetOverlapCheckingEnable) return null;
    
    try {
      const existingTimesheets = await store.dispatch('v2/loadTimesheetsByDate', {
        date: dayjs(startDate).format('YYYY-MM-DD'),
        excludeId: excludeTimesheetId
      });

      if (!existingTimesheets?.length) return null;

      const currentRanges: TimeRange[] = lines
        .filter(l => l.startTime && l.endTime)
        .map(l => ({
          startMinutes: toMinutes(l.startTime),
          endMinutes: toMinutes(l.endTime)
        }));

      for (const ts of existingTimesheets) {
        const tsLines = ts.TimesheetLines || ts.timesheetLines || [];
        for (const line of tsLines) {
          const existingRange: TimeRange = {
            startMinutes: line.StartTime ?? line.startTime ?? 0,
            endMinutes: line.EndTime ?? line.endTime ?? 0
          };

          for (const current of currentRanges) {
            if (rangesOverlap(current, existingRange)) {
              return {
                isValid: false,
                type: 'error',
                title: 'Submitting',
                message: "Overlapping tasks detected with\n another timesheet. Ensure no conflicts in\n your specified task times with other\n timesheet's task times",
                hasConfirmOption: false
              };
            }
          }
        }
      }
    } catch (error) {
      // Error checking overlap
    }
    return null;
  };

  // 5. Check night shift mismatch (Day shift passes midnight)
  const checkNightShiftMismatch = (lines: TimesheetEntryLine[], isNightShift: boolean): ValidationResult | null => {
    if (!settings.value.PassMidnightCheckingEnable) return null;
    if (isNightShift) return null; // Only check for day shift
    
    for (const line of lines) {
      if (!line.startTime || !line.endTime) continue;
      
      const startMinutes = toMinutes(line.startTime);
      const endMinutes = toMinutes(line.endTime);
      
      // Day shift but time crosses midnight (PM start, AM end) or (PM start, PM end but end < start)
      const isPMStart = startMinutes >= 720; // 12:00 PM
      const isAMEnd = endMinutes < 720;
      
      if ((isPMStart && isAMEnd) || (isPMStart && endMinutes < startMinutes)) {
        return {
          isValid: false,
          type: 'error',
          title: 'Submitting',
          message: "You have entered time for a Day shift that\n passes midnight. Please change the\n shift type to 'Night Shift' or correct the\n task time before saving or submitting.",
          hasConfirmOption: false
        };
      }
    }
    return null;
  };

  // 6. Check day shift mismatch (Night shift passes midday)
  const checkDayShiftMismatch = (lines: TimesheetEntryLine[], isNightShift: boolean): ValidationResult | null => {
    if (!isNightShift) return null; // Only check for night shift
    
    for (const line of lines) {
      if (!line.startTime || !line.endTime) continue;
      
      const startMinutes = toMinutes(line.startTime);
      const endMinutes = toMinutes(line.endTime);
      
      // Night shift but time crosses midday (AM start, PM end)
      const isAMStart = startMinutes < 720;
      const isPMEnd = endMinutes >= 720;
      
      if (isAMStart && isPMEnd) {
        return {
          isValid: false,
          type: 'warning',
          title: 'Submitting',
          message: 'You have entered time for a Night shift \nthat passes midday.\n Please confirm this is accurate \nor go back and correct the timesheet.',
          hasConfirmOption: true
        };
      }
    }
    return null;
  };

  // Main validation function - runs all checks in order
  const validateAll = async (params: {
    lines: TimesheetEntryLine[];
    startDate: string;
    isNightShift: boolean;
    excludeTimesheetId?: number;
  }): Promise<ValidationResult | null> => {
    const { lines, startDate, isNightShift, excludeTimesheetId } = params;

    // 1. Check current timesheet total hours
    const totalHoursResult = checkCurrentTimesheetTotalHours(lines);
    if (totalHoursResult) return totalHoursResult;

    // 2. Check combined total hours
    const combinedHoursResult = await checkTimesheetTotalHours(lines, startDate, excludeTimesheetId);
    if (combinedHoursResult) return combinedHoursResult;

    // 3. Check intra-timesheet overlap
    const intraOverlapResult = checkIntraOverlap(lines);
    if (intraOverlapResult) return intraOverlapResult;

    // 4. Check cross-timesheet overlap
    const crossOverlapResult = await checkCrossOverlap(lines, startDate, excludeTimesheetId);
    if (crossOverlapResult) return crossOverlapResult;

    // 5. Check night shift mismatch
    const nightMismatchResult = checkNightShiftMismatch(lines, isNightShift);
    if (nightMismatchResult) return nightMismatchResult;

    // 6. Check day shift mismatch
    const dayMismatchResult = checkDayShiftMismatch(lines, isNightShift);
    if (dayMismatchResult) return dayMismatchResult;

    return null;
  };

  return {
    calculateTotalMinutes,
    checkCurrentTimesheetTotalHours,
    checkTimesheetTotalHours,
    checkIntraOverlap,
    checkCrossOverlap,
    checkNightShiftMismatch,
    checkDayShiftMismatch,
    validateAll
  };
}
